"use client";
import { toast } from "@/app/providers";
import {
  getPersonalIntroductionVideoDetail,
  postCreateIntroductionVideo,
  postUpdateIntroductionVideo,
  putUpdateIntroductionVideo,
} from "@/services/api/personal";
import { PERSONAL_MY_PAGE } from "@/services/constants/routerPath";
import Yup from "@/services/constants/validateSchema";
import {
  AspectRatio,
  Box,
  Button,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";

import YoutubeVideoPlayerIframe from "@/app/_components/share/YoutubeVideoPlayerIframe";
import { description, title, url } from "@/services/constants/validateSchema";
import useForm from "@/services/hooks/useForm";

export default function PersonalIntroductionVideoForm({
  id,
  isHideHeader,
  closeModal,
  refetch,
}) {
  const { t } = useTranslation();
  const router = useRouter();

  const { data } = useQuery(
    ["personalIntroductionVideo", id],
    async () => getPersonalIntroductionVideoDetail(id),
    { enabled: !!id }
  );

  const mutation = useMutation(async (video) => {
    if (video.id) {
      return putUpdateIntroductionVideo(video.id, video);
    }
    let result = await postCreateIntroductionVideo(video);
    if (result) {
      await postUpdateIntroductionVideo(result._id, video);
    }

    return result;
  });

  const initialValues = data || {
    title: "",
    description: "",
    url: "",
  };

  const validationSchema = Yup.object().shape({
    title,
    url,
    description,
  });

  const handleOnSubmit = (values) => {
    mutation.mutate(values, {
      onSuccess: () => {
        toast({
          title: t(id ? "Update successfully" : "Add successfully"),
          status: "success",
        });
        isHideHeader
          ? closeModal()
          : router.push(PERSONAL_MY_PAGE.INTRODUCTION_VIDEO);
      },
    });
  };

  const formik = useForm({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: handleOnSubmit,
    enableReinitialize: !!id,
  });

  return (
    <Stack as="form" onSubmit={formik.handleSubmit} gap={3} noValidate>
      {!isHideHeader && (
        <HStack
          bg="white"
          position="sticky"
          top={0}
          zIndex={1}
          justifyContent="space-between"
          rounded="md"
          bgColor="white"
          p={4}
        >
          <Text fontWeight="semibold" fontSize="lg">
            {id ? t("Edit introduction video") : t("Add new video")}
          </Text>

          <HStack>
            <Button variant="outline" onClick={() => router.back()}>
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              isLoading={mutation.isLoading}
              loadingText={t("Saving...")}
            >
              {t("Save")}
            </Button>
          </HStack>
        </HStack>
      )}
      <Box bgColor="white" rounded="md" px={{ lg: 4 }} py={{ lg: "60px" }}>
        <Container maxW="container.xl">
          <Grid
            templateColumns={{
              base: "100%",
              md: "repeat(2, 1fr)",
              lg: "repeat(5, 1fr)",
            }}
            gap={{ lg: 4 }}
            rowGap={4}
          >
            <GridItem colSpan={3}>
              <Stack gap={3}>
                <FormControl
                  isRequired
                  isInvalid={formik.touched.title && formik.errors.title}
                >
                  <FormLabel>{t("Video title")}</FormLabel>
                  <Input
                    name="title"
                    placeholder={t("Enter video title")}
                    value={formik.values.title}
                    onChange={formik.handleChange}
                  />
                  <FormErrorMessage>{formik.errors.title}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isRequired
                  isInvalid={formik.touched.url && formik.errors.url}
                >
                  <FormLabel>{t("Youtube link")}</FormLabel>
                  <Input
                    name="url"
                    placeholder={t("Enter Youtube link")}
                    value={formik.values.url}
                    onChange={formik.handleChange}
                  />
                  <FormErrorMessage>{formik.errors.url}</FormErrorMessage>
                </FormControl>
                <FormControl>
                  <FormLabel>{t("Video description")}</FormLabel>
                  <Textarea
                    name="description"
                    placeholder={t("Enter video description")}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                  />
                </FormControl>
              </Stack>
            </GridItem>
            <GridItem colSpan={2}>
              <Stack h="full" w="full">
                <Text as="strong">{t("Preview")}</Text>
                <AspectRatio
                  overflow="hidden"
                  rounded="md"
                  w="full"
                  ratio={16 / 9}
                  bgColor="main"
                >
                  {formik.values.url ? (
                    <YoutubeVideoPlayerIframe url={formik.values.url} />
                  ) : (
                    <Stack h="full" w="full" bg="faded"></Stack>
                  )}
                </AspectRatio>
              </Stack>
            </GridItem>
          </Grid>
        </Container>
      </Box>
      {isHideHeader && (
        <HStack
          bg="white"
          rounded="md"
          bgColor="white"
          p={4}
          alignItems="center"
          w="full"
          justifyContent="center"
        >
          <Button
            type="submit"
            isLoading={mutation.isLoading}
            loadingText={t("Saving...")}
          >
            {t("Save")}
          </Button>
          <Button variant="outline" onClick={() => closeModal()}>
            {t("Cancel")}
          </Button>
        </HStack>
      )}
    </Stack>
  );
}
